import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMatchHistoryDetail } from '@tark/shared/hooks';
import { commandRatingVerification, getErrorMessage, isApiError } from '@tark/shared/lib';
import type {
  RatingVerification,
  RatingVerificationAction,
  RatingVerificationReason,
} from '@tark/shared/types';
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  CircleDot,
  Clock3,
  Flag,
  History,
  Loader2,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Users,
  XCircle,
} from 'lucide-react';

export default function MatchHistoryDetailPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMatchHistoryDetail(matchId);
  const [ratingActionPending, setRatingActionPending] = useState(false);
  const [ratingActionError, setRatingActionError] = useState<string | null>(null);
  const [showIssueReasons, setShowIssueReasons] = useState(false);
  const goBack = () => navigate((location.state as { from?: string } | null)?.from || '/play');

  const submitRatingAction = async (
    action: RatingVerificationAction,
    reasonCode?: RatingVerificationReason
  ) => {
    const verification = query.data?.ratingVerification;
    const correctionAction = action === 'ORGANIZER_ACCEPT_CORRECTION'
      || action === 'ORGANIZER_REJECT_CORRECTION';
    const resultVersionId = correctionAction
      ? verification?.pendingCorrectionResultVersionId
      : verification?.resultVersionId;
    if (!resultVersionId || ratingActionPending) return;

    setRatingActionPending(true);
    setRatingActionError(null);
    try {
      await commandRatingVerification(resultVersionId, {
        clientCommandId: crypto.randomUUID(),
        action,
        ...(reasonCode ? { reasonCode } : {}),
      });
      setShowIssueReasons(false);
      await query.refetch();
    } catch (error) {
      const status = isApiError(error) ? error.response?.status : undefined;
      if (status && status >= 400 && status < 500) {
        setRatingActionError('This result is no longer current. We refreshed the latest version.');
        await query.refetch();
      } else {
        setRatingActionError(getErrorMessage(error) || 'We could not update this result. Please try again.');
      }
    } finally {
      setRatingActionPending(false);
    }
  };

  if (query.isLoading) return <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (query.isError || !query.data) return <div className="mx-auto max-w-3xl py-8">
    <button type="button" onClick={goBack} className="inline-flex items-center gap-2 text-sm font-semibold text-text"><ArrowLeft className="h-4 w-4" />Matches</button>
    <div className="mt-8 border-y border-border py-12 text-center">
      <p className="font-semibold text-text">Match details are unavailable</p>
      <p className="mt-1 text-sm text-text-muted">The result may have changed, or it is no longer shared with your account.</p>
      <button type="button" onClick={() => void query.refetch()} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary"><RefreshCw className="h-4 w-4" />Try again</button>
    </div>
  </div>;

  const { match, sideA, sideB, completionReason } = query.data;
  const played = new Intl.DateTimeFormat(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    hour: match.timePrecision === 'DATE_ONLY' ? undefined : 'numeric',
    minute: match.timePrecision === 'DATE_ONLY' ? undefined : '2-digit',
    timeZone: match.timezone || 'UTC',
  }).format(new Date(match.playedAt));

  return <div className="mx-auto max-w-3xl space-y-7 pb-24 md:pb-10">
    <button type="button" onClick={goBack} className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-text hover:text-primary">
      <ArrowLeft className="h-4 w-4" />Matches
    </button>

    <header>
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        <CircleDot className="h-4 w-4 text-primary" />
        <span>{match.discipline}</span>
        {match.source === 'HISTORICAL_IMPORT' ? <span>· Imported</span> : null}
        {match.correctionState === 'CORRECTED' ? <span className="text-warning">· Corrected</span> : null}
      </div>
      <h1 className="mt-3 break-words text-3xl font-bold leading-tight text-text">{match.title}</h1>
      <p className="mt-2 text-sm text-text-muted">{match.squadName}</p>
    </header>

    <section className="grid gap-3 border-y border-border py-5 text-sm text-text-muted sm:grid-cols-2">
      <p className="flex items-center gap-2"><Calendar className="h-4 w-4" />{played}</p>
      <p className="flex items-center gap-2"><Users className="h-4 w-4" />{match.formatLabel || match.discipline}</p>
      {match.courtName ? <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{match.courtName}</p> : null}
      {completionReason ? <p className="flex items-center gap-2"><Clock3 className="h-4 w-4" />{humanize(completionReason)}</p> : null}
    </section>

    <section aria-labelledby="score-heading">
      <div className="flex items-end justify-between gap-4">
        <div><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Result</p><h2 id="score-heading" className="mt-1 text-xl font-semibold text-text">Score</h2></div>
        {match.score ? <p className="text-xl font-bold text-text">{match.score}</p> : null}
      </div>
      <div className="mt-4 divide-y divide-border border-y border-border">
        <SideRow label="Side 1" players={sideA.players} winner={sideA.winner} />
        <SideRow label="Side 2" players={sideB.players} winner={sideB.winner} />
      </div>
    </section>

    {query.data.ratingVerification ? (
      <RatingVerificationPanel
        verification={query.data.ratingVerification}
        pending={ratingActionPending}
        error={ratingActionError}
        showIssueReasons={showIssueReasons}
        onToggleIssueReasons={() => setShowIssueReasons((visible) => !visible)}
        onAction={submitRatingAction}
      />
    ) : null}

    <section className="flex items-start gap-3 border-t border-border pt-5 text-sm text-text-muted">
      <History className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{match.source === 'HISTORICAL_IMPORT'
        ? `Imported by a squad organizer${match.correctionState === 'CORRECTED' ? ` and corrected to version ${match.version}` : ''}.`
        : 'Recorded from a completed TARK session.'}</p>
    </section>
  </div>;
}

function RatingVerificationPanel({
  verification,
  pending,
  error,
  showIssueReasons,
  onToggleIssueReasons,
  onAction,
}: {
  verification: RatingVerification;
  pending: boolean;
  error: string | null;
  showIssueReasons: boolean;
  onToggleIssueReasons: () => void;
  onAction: (action: RatingVerificationAction, reasonCode?: RatingVerificationReason) => Promise<void>;
}) {
  const actions = new Set(verification.allowedActions);
  const issueAction: RatingVerificationAction | null = actions.has('REPORT_ISSUE')
    ? 'REPORT_ISSUE'
    : actions.has('DISPUTE')
      ? 'DISPUTE'
      : null;
  const status = verificationStatus(verification);

  return <section aria-labelledby="rating-verification-heading" className="border-y border-border py-5">
    <div className="flex items-start gap-3">
      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Rating</p>
        <h2 id="rating-verification-heading" className="mt-1 text-lg font-semibold text-text">
          {status.title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-text-muted">{status.description}</p>
      </div>
    </div>

    {verification.allowedActions.length > 0 ? (
      <div className="mt-4 flex flex-wrap gap-2">
        {actions.has('ACCEPT') ? (
          <ActionButton pending={pending} onClick={() => onAction('ACCEPT')} icon={CheckCircle2}>
            Verify score
          </ActionButton>
        ) : null}
        {actions.has('ORGANIZER_ACCEPT') ? (
          <ActionButton
            pending={pending}
            onClick={() => onAction('ORGANIZER_ACCEPT', 'SCORECARD_REVIEWED')}
            icon={CheckCircle2}
          >
            Verify as admin
          </ActionButton>
        ) : null}
        {issueAction ? (
          <button
            type="button"
            disabled={pending}
            aria-expanded={showIssueReasons}
            onClick={onToggleIssueReasons}
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-border px-3.5 py-2 text-sm font-semibold text-text hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Flag className="h-4 w-4" aria-hidden="true" />
            Report issue
          </button>
        ) : null}
        {actions.has('ORGANIZER_RETAIN') ? (
          <ActionButton
            pending={pending}
            onClick={() => onAction('ORGANIZER_RETAIN', 'REVIEW_CLEARED')}
            icon={ShieldCheck}
          >
            Keep original
          </ActionButton>
        ) : null}
        {actions.has('ORGANIZER_REJECT') ? (
          <button
            type="button"
            disabled={pending}
            onClick={() => void onAction('ORGANIZER_REJECT', 'INVALID_SCORECARD')}
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-red-300 px-3.5 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            <XCircle className="h-4 w-4" aria-hidden="true" />
            Reject score
          </button>
        ) : null}
        {actions.has('ORGANIZER_ACCEPT_CORRECTION') ? (
          <ActionButton
            pending={pending}
            onClick={() => onAction('ORGANIZER_ACCEPT_CORRECTION', 'CORRECTION_REVIEWED')}
            icon={CheckCircle2}
          >
            Accept correction
          </ActionButton>
        ) : null}
        {actions.has('ORGANIZER_REJECT_CORRECTION') ? (
          <button
            type="button"
            disabled={pending}
            onClick={() => void onAction('ORGANIZER_REJECT_CORRECTION', 'CORRECTION_REJECTED')}
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-border px-3.5 py-2 text-sm font-semibold text-text hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            <XCircle className="h-4 w-4" aria-hidden="true" />
            Keep original
          </button>
        ) : null}
      </div>
    ) : null}

    {showIssueReasons && issueAction ? (
      <div className="mt-4 border-l-2 border-primary/40 pl-3" aria-label="Choose what is wrong">
        <p className="text-sm font-semibold text-text">What needs review?</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {ISSUE_REASONS.map((reason) => (
            <button
              key={reason.code}
              type="button"
              disabled={pending}
              onClick={() => void onAction(issueAction, reason.code)}
              className="min-h-10 rounded-md border border-border px-3 py-2 text-left text-sm font-medium text-text hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              {reason.label}
            </button>
          ))}
        </div>
      </div>
    ) : null}

    {pending ? <p className="mt-3 inline-flex items-center gap-2 text-sm text-text-muted"><Loader2 className="h-4 w-4 animate-spin" />Updating result…</p> : null}
    {error ? <p role="alert" className="mt-3 text-sm font-medium text-red-700 dark:text-red-300">{error}</p> : null}
  </section>;
}

const ISSUE_REASONS: Array<{ code: RatingVerificationReason; label: string }> = [
  { code: 'SCORE_INCORRECT', label: 'Score is incorrect' },
  { code: 'WRONG_PLAYERS', label: 'Wrong players' },
  { code: 'DUPLICATE_RESULT', label: 'Duplicate result' },
  { code: 'OTHER_RESULT_CONCERN', label: 'Something else' },
];

function ActionButton({
  pending,
  onClick,
  icon: Icon,
  children,
}: {
  pending: boolean;
  onClick: () => Promise<void>;
  icon: typeof CheckCircle2;
  children: string;
}) {
  return <button
    type="button"
    disabled={pending}
    onClick={() => void onClick()}
    className="inline-flex min-h-10 items-center gap-2 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
  >
    <Icon className="h-4 w-4" aria-hidden="true" />
    {children}
  </button>;
}

function verificationStatus(verification: RatingVerification) {
  switch (verification.verificationState) {
    case 'VERIFIED':
      return {
        title: 'Verified for rating',
        description: 'This score can contribute to TARK ratings.',
      };
    case 'UNDER_REVIEW':
      return {
        title: 'Score under review',
        description: 'Its rating effect is paused until a squad admin resolves the issue.',
      };
    case 'REJECTED':
      return {
        title: 'Not used for rating',
        description: 'This result remains in match history but does not affect ratings.',
      };
    case 'HISTORY_ONLY':
      return {
        title: 'Match history only',
        description: 'This result is visible in history and is not eligible for rating.',
      };
    default:
      return {
        title: 'Waiting for verification',
        description: 'One opposing player or a squad admin can verify this score.',
      };
  }
}

function SideRow({ label, players, winner }: { label: string; players: string[]; winner: boolean }) {
  return <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-4">
    <div><p className="text-xs text-text-subtle">{label}</p><p className="mt-1 break-words font-semibold text-text">{players.join(' & ') || 'Players unavailable'}</p></div>
    {winner ? <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">Winner</span> : null}
  </div>;
}

function humanize(value: string) {
  return value.toLowerCase().replace(/_/g, ' ').replace(/^./, (letter: string) => letter.toUpperCase());
}
